import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Download, Loader2, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { RootsTabs } from "@/components/RootsTabs";

export const BRAND_DOC_BLURB = "A personalised brand foundations document, generated at onboarding using Lindsay's methodology and saved in your portal — ready to use as context in any AI tool.";

const PROMPTS = [
  ["Instagram captions", "Using my Brand Foundations above, write 5 Instagram captions for this week, one per content pillar. Keep my tone of voice, open each with a scroll-stopping first line, and end with one clear call to action. Topic or product this week: [add here]"],
  ["TikTok and Reels ideas", "Using my Brand Foundations above, give me 10 short-form video ideas for TikTok and Reels. For each: the hook for the first second, what to film on my phone, and the caption. Make them feel native and behind-the-scenes, not polished ads."],
  ["Launch campaign plan", "Using my Brand Foundations above, plan a 4-week launch campaign for [product, menu or event]. Include the key message, a week-by-week plan across social, email and in-store or in-venue, and 3 post ideas per week."],
  ["Email newsletter", "Using my Brand Foundations above, write this month's email newsletter to my customers. Include 3 subject line options, a warm opening, one story from behind the scenes, one offer or news item, and a clear call to action. News to include: [add here]"],
  ["Seasonal campaign", "Using my Brand Foundations above, give me 3 campaign ideas for [season or date, e.g. Christmas, Valentine's, summer]. For each: the idea, why it fits my ideal customer, and the posts and email to run it."],
  ["Partnership pitch", "Using my Brand Foundations above, write a short, friendly outreach email to [partner brand or venue] proposing a collaboration. Lead with what's in it for them, pitch one specific idea and make it easy to say yes."],
  ["Website and bio copy", "Using my Brand Foundations above, write my Instagram bio (under 150 characters), a one-line website headline and a 3-sentence About section, all in my tone of voice."],
  ["Content calendar", "Using my Brand Foundations above, create a 2-week content calendar as a table: date, platform, content pillar, idea and caption starter. Mix my pillars evenly and include one post that invites customers to reply or share."],
];

const Prompts = ({ doc }) => {
  const copy = (p, withDoc) => navigator.clipboard.writeText(withDoc ? `${doc}\n\n---\n\n${p}` : p).then(() => toast.success(withDoc ? "Copied with your Brand Foundations. Paste into ChatGPT or Claude." : "Prompt copied"));
  return (
    <section className="mt-10" data-testid="brand-prompts">
      <h2 className="font-serif uppercase text-2xl text-navy">Ready-made prompts</h2>
      <p className="text-sm text-muted-foreground mt-1 mb-5">Copy a prompt with your Brand Foundations attached, paste it into ChatGPT or Claude, and fill in the [brackets].</p>
      <div className="grid sm:grid-cols-2 gap-4">{PROMPTS.map(([t, p], i) => (
        <div key={t} className="rounded-2xl border border-border bg-card p-5 flex flex-col" data-testid={`brand-prompt-${i}`}>
          <div className="font-medium text-navy">{t}</div>
          <p className="text-xs text-muted-foreground mt-2 flex-1">{p}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" onClick={() => copy(p, true)} data-testid={`brand-prompt-copy-doc-${i}`} className="rounded-full bg-pinkb hover:bg-pinkb/90 text-white h-8 text-xs"><Copy className="h-3.5 w-3.5 mr-1" />Copy with my Brand Foundations</Button>
            <Button size="sm" variant="ghost" onClick={() => copy(p, false)} data-testid={`brand-prompt-copy-${i}`} className="rounded-full h-8 text-xs">Prompt only</Button>
          </div>
        </div>
      ))}</div>
    </section>
  );
};

const Locked = () => (
  <div className="rounded-3xl bg-navy text-white p-8" data-testid="brand-doc-locked">
    <Lock className="h-6 w-6 text-sun" />
    <h2 className="font-serif uppercase text-2xl mt-3">Part of The Roots Club Pro</h2>
    <p className="mt-2 text-white/85 max-w-xl">{BRAND_DOC_BLURB}</p>
    <Link to="/app/membership" data-testid="brand-doc-upgrade" className="mt-6 inline-block rounded-full bg-pinkb hover:bg-pinkb/90 px-6 py-2.5 text-sm transition-colors">See Pro</Link>
  </div>
);

const Questionnaire = ({ questions, onDone }) => {
  const [a, setA] = useState({});
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/me/brand-doc", { answers: a }); onDone(); } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-8" data-testid="brand-doc-form">
      <p className="rounded-xl bg-secondary p-4 text-sm">About 15 minutes. Short answers are fine. Lindsay's method turns them into your mission, positioning, ideal customer, tone of voice and content pillars. It's created once, so take your time.</p>
      {questions.map((s) => (
        <fieldset key={s.section} className="rounded-2xl border border-border bg-card p-6 space-y-5">
          <legend className="font-serif uppercase text-xl text-navy px-2">{s.section}</legend>
          {s.items.map((q) => (
            <label key={q.id} className="block text-sm">
              <span className="font-medium text-navy">{q.q}</span>
              <Textarea required rows={2} value={a[q.id] || ""} onChange={(e) => setA({ ...a, [q.id]: e.target.value })} data-testid={`brand-q-${q.id}`} className="mt-2 bg-white" />
            </label>
          ))}
        </fieldset>
      ))}
      <Button type="submit" disabled={busy} data-testid="brand-doc-submit" className="rounded-full bg-pinkb hover:bg-pinkb/90 text-white px-8">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}Create my Brand Foundations
      </Button>
    </form>
  );
};

const Doc = ({ d }) => {
  const copy = () => navigator.clipboard.writeText(d.text).then(() => toast.success("Copied. Paste it into any AI tool as context."));
  const download = () => {
    const url = URL.createObjectURL(new Blob([d.text], { type: "text/plain;charset=utf-8" }));
    const el = Object.assign(document.createElement("a"), { href: url, download: `${(d.business_name || "brand").replace(/[^\w-]+/g, "-").toLowerCase()}-brand-foundations.txt` });
    el.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div data-testid="brand-doc-ready">
      <div className="flex flex-wrap gap-3 mb-4">
        <Button onClick={copy} data-testid="brand-doc-copy" className="rounded-full bg-navy hover:bg-navy/90 text-white"><Copy className="h-4 w-4 mr-2" />Copy all</Button>
        <Button onClick={download} variant="outline" data-testid="brand-doc-download" className="rounded-full"><Download className="h-4 w-4 mr-2" />Download .txt</Button>
      </div>
      <p className="text-xs text-muted-foreground mb-3">Paste this into Claude, ChatGPT or any AI tool before asking for content or campaigns, so everything sounds like your brand.</p>
      <pre className="whitespace-pre-wrap break-words rounded-2xl border border-border bg-card p-6 text-sm leading-relaxed font-sans max-h-[60vh] overflow-y-auto" data-testid="brand-doc-text">{d.text}</pre>
      {d.eligible && <Prompts doc={d.text} />}
    </div>
  );
};

export default function BrandFoundations() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/me/brand-doc").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (d?.status !== "generating") return undefined;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [d, load]);
  if (!d) return null;
  return (
    <div className="max-w-3xl">
      <PageHeader eyebrow="Pro · Brand Foundations Document" title="Your Brand Foundations." />
      <RootsTabs />
      {!d.eligible && d.status !== "ready" ? <Locked />
        : d.status === "ready" ? <Doc d={d} />
        : d.status === "generating" ? <div className="rounded-2xl border border-border bg-card p-8 flex items-center gap-3" data-testid="brand-doc-generating"><Loader2 className="h-5 w-5 animate-spin text-pinkb" />Lindsay's method is building your document. This takes about a minute.</div>
        : <>{d.status === "failed" && <p className="mb-4 rounded-xl bg-rose-50 border border-rose-200 p-4 text-sm" data-testid="brand-doc-failed">Something went wrong creating your document. Please submit again.</p>}
          <Questionnaire questions={d.questions} onDone={load} /></>}
    </div>
  );
}
